/**
 * Main Layout Component
 * Integrates: Navigation + KPIs + Sidebar + Content
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navigation from './Navigation';
import Breadcrumbs from './Breadcrumbs';
import Sidebar from './Sidebar';
import AppLauncher from './AppLauncher';
import AppModal from './AppModal';
import MenuCustomizer from './MenuCustomizer';
import RichTextEditor from './RichTextEditor';
import FeedbackAttachmentsField from './FeedbackAttachmentsField';
import api from '../services/api';
import { CATEGORY_OPTIONS, PRIORITY_OPTIONS, usuarioActual } from './feedbackConfig';
import { buildFavoriteFromMenuItem } from '../menuConfig';
import { TutorialProvider } from '../tutorial/TutorialContext';
import GuidedTour from '../tutorial/GuidedTour';
import './MainLayout.css';

/**
 * Rutas que ocupan TODO el alto y ancho del layout (diseñadores, mapas de
 * planta, visor de rack, canvas de red y Tomas ↔ panel): sin el relleno de
 * `.main-content` ni el tope de 1400 px, con el alto repartido por flex.
 *
 * Se comparan PREFIJOS y no con una regex: `^\/infrastructure\/network-canvas\/`
 * no casaba con la ruta sin barra final y la página se quedaba con el relleno.
 */
const CANVAS_ROUTE_BASES = [
  '/cabling/designer', '/cabling/wiring', '/cabling/map', '/cabling/canvas',
  '/wifi/map', '/cameras/map',
  '/infrastructure/rack', '/rack',
  '/infrastructure/network-canvas',
];

const MainLayout = ({ children, userInfo, selectedCompany, authorizedCompanies = [], onCompanyChange, onLogout, sessionExpired, onReconnect }) => {
  // Los diseñadores y los mapas de planta necesitan TODO el alto y ancho: se les
  // quita el relleno del layout y el tope de 1400 px.
  //
  // `/cameras/map` faltaba: es el MISMO `FloorPlanMap` que el del WiFi, pero al
  // no entrar en esta lista se quedaba con el `padding: 2rem` del layout, el
  // `max-width: 1400px` y un wrapper sin alto, así que el mapa aparecía
  // "cortado" (solo la barra de herramientas) en vez de a pantalla completa.
  //
  // El visor de rack y los lienzos (Canvas de red, Tomas ↔ panel) entran por el
  // mismo motivo: son pantallas de trabajo y con el relleno del layout quedaban
  // como una tarjeta centrada con aire por los cuatro lados. Aquí toman el alto
  // completo entre la barra de migas y el borde inferior, y el ancho desde el
  // menú lateral.
  const location = useLocation();
  const pathname = location.pathname || '';
  const isCanvasRoute = CANVAS_ROUTE_BASES.some(
    (base) => pathname === base || pathname.startsWith(`${base}/`)
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [menuPrefs, setMenuPrefs] = useState({ company_hidden: [], user_hidden: [], hidden: [], menu_mode: 'sections' });
  const [menuCustomizerOpen, setMenuCustomizerOpen] = useState(false);
  const [menuCustomizerTab, setMenuCustomizerTab] = useState('menu');
  const [menuSaving, setMenuSaving] = useState(false);
  const [menuError, setMenuError] = useState('');
  const [favorites, setFavorites] = useState({ global: [], company: [] });
  const [favoritesSaving, setFavoritesSaving] = useState(false);
  const [alertsFeed, setAlertsFeed] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState('');
  const [feedbackForm, setFeedbackForm] = useState({
    title: '',
    description: '',
    descriptionHtml: '',
    category: 'suggestion',
    priority: 'medium',
    roadmap_target: '',
    reporter_name: '',
  });
  const [feedbackAttachments, setFeedbackAttachments] = useState([]);

  const toggleLauncher = useCallback(() => setLauncherOpen(v => !v), []);
  const closeLauncher  = useCallback(() => setLauncherOpen(false), []);

  const isSuperadmin = Boolean(userInfo?.is_superadmin);

  const companyOptions = useMemo(() => {
    const mapped = (authorizedCompanies || []).map((company) => {
      if (company && typeof company === 'object') {
        const id = company.id || company.slug || company.code || company.company_id || company.value || company.company || '';
        const label = company.name || company.company_name || company.display_name || company.label || company.slug || company.code || company.id || company.company_id || company.company || 'Empresa';
        return { id: id?.toString(), label };
      }
      const id = company?.toString();
      const label = id ? `Empresa ${id}` : 'Empresa';
      return { id, label };
    });
    // Orden alfabético (respetando acentos y mayúsculas del español).
    return mapped.sort((a, b) =>
      String(a.label || '').localeCompare(String(b.label || ''), 'es', { sensitivity: 'base', numeric: true })
    );
  }, [authorizedCompanies]);

  const currentCompany = useMemo(() => {
    if (!companyOptions.length) return null;
    if (selectedCompany) {
      const found = companyOptions.find((c) => c.id?.toString() === selectedCompany?.toString());
      if (found) return found;
    }
    if (companyOptions.length === 1) return companyOptions[0];
    return null;
  }, [companyOptions, selectedCompany]);

  // Empresa con la que se guardan las preferencias de menú: normalmente la
  // seleccionada, pero si aún no hay ninguna elegida vale la activa (y en la
  // vista "todas las empresas" del superadmin no hay menú que personalizar).
  const menuCompanyId = useMemo(
    () =>
      selectedCompany && selectedCompany !== 'all'
        ? selectedCompany
        : currentCompany?.id ?? null,
    [selectedCompany, currentCompany]
  );

  // ── Visibilidad del menú lateral: política de la empresa + preferencia del
  // usuario para la empresa activa. Se recarga al cambiar de empresa, que es
  // justo lo que hace falta para el usuario multiempresa.
  useEffect(() => {
    let cancelled = false;
    if (!menuCompanyId) {
      setMenuPrefs({ company_hidden: [], user_hidden: [], hidden: [], menu_mode: 'sections' });
      setFavorites({ global: [], company: [] });
      return undefined;
    }
    api.getMenuPreferences(menuCompanyId)
      .then((data) => {
        if (!cancelled) setMenuPrefs(data);
      })
      .catch(() => {
        // Si falla no bloqueamos la app: el menú se muestra completo.
        if (!cancelled) setMenuPrefs({ company_hidden: [], user_hidden: [], hidden: [], menu_mode: 'sections' });
      });
    api.getFavorites(menuCompanyId)
      .then((data) => {
        if (!cancelled) setFavorites(data);
      })
      .catch(() => {
        if (!cancelled) setFavorites({ global: [], company: [] });
      });
    return () => { cancelled = true; };
  }, [menuCompanyId]);

  const handleSaveMenuPreferences = useCallback(async ({ hiddenKeys, menuMode } = {}) => {
    if (!menuCompanyId) return;
    setMenuSaving(true);
    setMenuError('');
    try {
      const data = await api.saveMenuPreferences(menuCompanyId, hiddenKeys, menuMode);
      setMenuPrefs(data);
      setMenuCustomizerOpen(false);
    } catch (error) {
      setMenuError(error.message || 'No se pudo guardar la configuración del menú');
    } finally {
      setMenuSaving(false);
    }
  }, [menuCompanyId]);

  // ── Favoritos ────────────────────────────────────────────────────────────
  // Se guarda la lista completa del ámbito que cambia (máx. 20 filas): es más
  // simple y no deja huecos en el orden.
  const persistFavorites = useCallback(async (scope, list) => {
    if (!menuCompanyId) return;
    setFavoritesSaving(true);
    setMenuError('');
    try {
      const data = await api.saveFavorites(menuCompanyId, scope, list);
      setFavorites(data);
    } catch (error) {
      setMenuError(error.message || 'No se pudieron guardar los favoritos');
    } finally {
      setFavoritesSaving(false);
    }
  }, [menuCompanyId]);

  const handleAddFavorite = useCallback((scope, item) => {
    const bucket = scope === 'global' ? 'global' : 'company';
    const favorite = buildFavoriteFromMenuItem(item);
    if (!favorite) return;
    const current = favorites[bucket] || [];
    if (current.some((row) => row.menu_key === favorite.menu_key)) return;
    persistFavorites(bucket, [...current, favorite]);
  }, [favorites, persistFavorites]);

  const handleRemoveFavorite = useCallback((favorite) => {
    const bucket = favorite?.scope === 'global' ? 'global' : 'company';
    const current = favorites[bucket] || [];
    persistFavorites(
      bucket,
      current.filter((row) => row.menu_key !== favorite.menu_key)
    );
  }, [favorites, persistFavorites]);

  const handleSaveFavorites = useCallback(async ({ global: globalList = [], company: companyList = [] } = {}) => {
    if (!menuCompanyId) return;
    setFavoritesSaving(true);
    setMenuError('');
    try {
      // Un PUT por ámbito: el backend sustituye la lista completa de cada uno.
      // Se releen ambos al final para que el estado local refleje lo guardado
      // aunque una de las dos peticiones hubiera fallado a medias.
      await Promise.all([
        api.saveFavorites(menuCompanyId, 'company', companyList),
        api.saveFavorites(menuCompanyId, 'global', globalList),
      ]);
      const data = await api.getFavorites(menuCompanyId);
      setFavorites(data);
      setMenuCustomizerOpen(false);
    } catch (error) {
      setMenuError(error.message || 'No se pudieron guardar los favoritos');
    } finally {
      setFavoritesSaving(false);
    }
  }, [menuCompanyId]);

  const openMenuCustomizer = useCallback((tab = 'menu') => {
    setMenuError('');
    setMenuCustomizerTab(tab === 'favorites' ? 'favorites' : 'menu');
    setMenuCustomizerOpen(true);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const loadAlertsFeed = useCallback(async ({ silent = false } = {}) => {
    if (!selectedCompany) {
      setAlertsFeed(null);
      return;
    }
    if (!silent) setAlertsLoading(true);
    try {
      const response = await api.getDashboardAlertsFeed(selectedCompany, 10);
      setAlertsFeed(response);
    } catch {
      if (!silent) setAlertsFeed(null);
    } finally {
      if (!silent) setAlertsLoading(false);
    }
  }, [selectedCompany]);

  useEffect(() => {
    loadAlertsFeed();
  }, [loadAlertsFeed]);

  useEffect(() => {
    if (!selectedCompany) return undefined;
    const timerId = window.setInterval(() => {
      loadAlertsFeed({ silent: true });
    }, 60000);
    return () => window.clearInterval(timerId);
  }, [selectedCompany, loadAlertsFeed]);

  const openFeedbackModal = useCallback(() => {
    setFeedbackError('');
    setFeedbackSuccess('');
    setFeedbackModalOpen(true);
  }, []);

  const closeFeedbackModal = useCallback(() => {
    if (feedbackSaving) return;
    setFeedbackModalOpen(false);
    // Los adjuntos ya subidos que no se han llegado a vincular se borran al
    // cancelar: si no, quedarían huérfanos en disco y en la base de datos.
    if (feedbackAttachments.length && selectedCompany) {
      feedbackAttachments.forEach((attachment) => {
        api.deleteFeedbackAttachment(attachment.id, selectedCompany).catch(() => {});
      });
      setFeedbackAttachments([]);
    }
  }, [feedbackSaving, feedbackAttachments, selectedCompany]);

  const handleFeedbackSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!selectedCompany) {
      setFeedbackError('Selecciona una empresa antes de crear un ticket.');
      return;
    }
    const hasContent =
      Boolean(feedbackForm.description.trim()) ||
      /<img/i.test(feedbackForm.descriptionHtml || '') ||
      feedbackAttachments.length > 0;
    if (!feedbackForm.title.trim() || !hasContent) {
      setFeedbackError('El titulo y la descripcion son obligatorios.');
      return;
    }

    try {
      setFeedbackSaving(true);
      setFeedbackError('');
      await api.createFeedbackItem(
        {
          title: feedbackForm.title.trim(),
          description: feedbackForm.description.trim(),
          description_html: feedbackForm.descriptionHtml || null,
          attachment_ids: feedbackAttachments.map((item) => item.id),
          category: feedbackForm.category,
          priority: feedbackForm.priority,
          roadmap_target: feedbackForm.roadmap_target.trim() || null,
          reporter_name: feedbackForm.reporter_name.trim() || null,
        },
        selectedCompany
      );
      setFeedbackSuccess('Ticket creado correctamente.');
      setFeedbackForm((prev) => ({
        ...prev,
        title: '',
        description: '',
        descriptionHtml: '',
        roadmap_target: '',
      }));
      setFeedbackAttachments([]);
      setTimeout(() => {
        setFeedbackModalOpen(false);
      }, 450);
    } catch (err) {
      setFeedbackError(err.message || 'No se pudo crear el ticket.');
    } finally {
      setFeedbackSaving(false);
    }
  }, [feedbackForm, feedbackAttachments, selectedCompany]);

  return (
    <div className={`main-layout${isCanvasRoute ? ' is-canvas' : ''}`}>
      {/* Top Navigation with integrated Company Selector */}
      <Navigation 
        user={userInfo} 
        onLogout={onLogout}
        onToggleSidebar={toggleSidebar}
        onToggleLauncher={toggleLauncher}
        launcherOpen={launcherOpen}
        selectedCompany={selectedCompany}
        onCompanyChange={onCompanyChange}
        authorizedCompanies={authorizedCompanies}
        alertsFeed={alertsFeed}
        alertsLoading={alertsLoading}
        onOpenFeedbackQuickCreate={openFeedbackModal}
        favorites={favorites}
        onAddFavorite={handleAddFavorite}
        onRemoveFavorite={handleRemoveFavorite}
        favoriteSaving={favoritesSaving}
        favoriteEnabled={Boolean(menuCompanyId)}
      />

      {/* Ruta de navegación global: historial ← → y migas de pan */}
      <Breadcrumbs />

      {/* App Launcher Overlay */}
      <AppLauncher isOpen={launcherOpen} onClose={closeLauncher} />

      <AppModal
        open={feedbackModalOpen}
        onClose={closeFeedbackModal}
        title="Nuevo ticket de feedback"
        subtitle="Crea una incidencia, sugerencia o solicitud de roadmap sin salir de la pantalla actual"
        size="md"
      >
        <form className="quick-feedback-form" onSubmit={handleFeedbackSubmit}>
          {feedbackError && <div className="quick-feedback-error">{feedbackError}</div>}
          {feedbackSuccess && <div className="quick-feedback-success">{feedbackSuccess}</div>}

          {/* El autor lo pone el servidor desde la sesion; se dice para que no haya duda. */}
          <p className="quick-feedback-hint">
            El ticket queda <strong>a tu nombre</strong>
            {usuarioActual().nombre || usuarioActual().email
              ? <> ({usuarioActual().nombre || usuarioActual().email})</> : null}
            , así que en el Feedback Hub se ve quién lo abrió.
          </p>

          <label className="quick-feedback-label">
            Titulo
            <input
              className="quick-feedback-input"
              value={feedbackForm.title}
              onChange={(e) => setFeedbackForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Ej: Error al guardar rack con PDU"
              required
            />
          </label>

          <div className="quick-feedback-label">
            Descripcion
            <RichTextEditor
              companyId={selectedCompany}
              value={feedbackForm.descriptionHtml}
              onChange={(html, text) =>
                setFeedbackForm((prev) => ({ ...prev, descriptionHtml: html, description: text }))
              }
              onError={setFeedbackError}
              placeholder="Describe el contexto, impacto y resultado esperado. Acepta formato y puedes pegar capturas."
              minHeight={150}
            />
          </div>

          <div className="quick-feedback-label">
            Adjuntos
            <FeedbackAttachmentsField
              companyId={selectedCompany}
              attachments={feedbackAttachments}
              onUploaded={(attachment) => setFeedbackAttachments((prev) => [...prev, attachment])}
              onRemoved={(attachment) => {
                setFeedbackAttachments((prev) => prev.filter((item) => item.id !== attachment.id));
                if (selectedCompany) {
                  api.deleteFeedbackAttachment(attachment.id, selectedCompany).catch(() => {});
                }
              }}
              onError={setFeedbackError}
              hint="Capturas, logs, PDFs…"
            />
          </div>

          <div className="quick-feedback-grid">
            <label className="quick-feedback-label">
              Categoria
              <select
                className="quick-feedback-input"
                value={feedbackForm.category}
                onChange={(e) => setFeedbackForm((prev) => ({ ...prev, category: e.target.value }))}
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="quick-feedback-label">
              Prioridad
              <select
                className="quick-feedback-input"
                value={feedbackForm.priority}
                onChange={(e) => setFeedbackForm((prev) => ({ ...prev, priority: e.target.value }))}
              >
                {PRIORITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>

          {feedbackForm.category === 'roadmap' && (
            <label className="quick-feedback-label">
              Objetivo roadmap
              <input
                className="quick-feedback-input"
                value={feedbackForm.roadmap_target}
                onChange={(e) => setFeedbackForm((prev) => ({ ...prev, roadmap_target: e.target.value }))}
                placeholder="Ej: Q4 2026"
              />
            </label>
          )}

          <label className="quick-feedback-label">
            Reportado por (opcional)
            <input
              className="quick-feedback-input"
              value={feedbackForm.reporter_name}
              onChange={(e) => setFeedbackForm((prev) => ({ ...prev, reporter_name: e.target.value }))}
              placeholder="Nombre o equipo"
            />
          </label>

          <AppModal.Footer>
            <button
              type="button"
              className="quick-feedback-secondary"
              onClick={() => {
                setFeedbackModalOpen(false);
                window.location.assign('/feedback');
              }}
            >
              Abrir buzon
            </button>
            <button type="button" className="quick-feedback-secondary" onClick={closeFeedbackModal} disabled={feedbackSaving}>
              Cancelar
            </button>
            <button type="submit" className="quick-feedback-primary" disabled={feedbackSaving || !selectedCompany}>
              {feedbackSaving ? 'Guardando...' : 'Crear ticket'}
            </button>
          </AppModal.Footer>
        </form>
      </AppModal>

      {/* Personalizador del menú (por usuario y empresa) */}
      <MenuCustomizer
        open={menuCustomizerOpen}
        onClose={() => setMenuCustomizerOpen(false)}
        companyLabel={currentCompany?.label}
        companyHidden={menuPrefs.company_hidden}
        userHidden={menuPrefs.user_hidden}
        menuMode={menuPrefs.menu_mode || 'sections'}
        favorites={favorites}
        initialTab={menuCustomizerTab}
        isSuperadmin={isSuperadmin}
        saving={menuSaving || favoritesSaving}
        error={menuError}
        onSave={handleSaveMenuPreferences}
        onSaveFavorites={handleSaveFavorites}
      />

      {/* KPI Panel - DISABLED TEMPORARILY */}
      {/* <KPIPanel selectedCompanyId={selectedCompany} /> */}

      {/* Layout Container (Sidebar + Content) */}
      <div className="layout-container">
        {/* Sidebar */}
        <Sidebar 
          isOpen={sidebarOpen} 
          onToggle={toggleSidebar}
          currentCompany={currentCompany}
          hiddenKeys={menuPrefs.hidden}
          isSuperadmin={isSuperadmin}
          menuMode={menuPrefs.menu_mode || 'sections'}
          favorites={favorites}
          onRemoveFavorite={handleRemoveFavorite}
          onOpenMenuCustomizer={openMenuCustomizer}
        />

        {/* Main Content Area */}
        <main className={`main-content${isCanvasRoute ? ' is-canvas' : ''}`}>
          {sessionExpired && (
            <div style={{
              background: 'rgba(245,158,11,.15)', color: '#fcd34d', border: '1px solid rgba(245,158,11,.4)',
              borderRadius: '6px', padding: '10px 18px', margin: '12px 20px 0',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              fontWeight: 500, fontSize: '0.95rem'
            }}>
              <span>⚠️ Tu sesión ha expirado. Los datos pueden no cargarse correctamente.</span>
              <button
                onClick={onReconnect}
                style={{
                  background: '#0d6efd', color: '#fff', border: 'none',
                  borderRadius: '4px', padding: '5px 14px', cursor: 'pointer',
                  fontWeight: 600, fontSize: '0.85rem'
                }}
              >
                🔄 Reconectar
              </button>
            </div>
          )}
          <div className={`content-wrapper${isCanvasRoute ? ' is-canvas' : ''}`}>
            {/* El tutorial envuelve TODA la aplicación: la página de tutoriales
                necesita el contexto, y el motor (que se pinta encima de todo,
                incluido el overlay de la ficha de servicio) resalta elementos
                de cualquier pantalla. */}
            <TutorialProvider companyId={selectedCompany}>
              {children}
              <GuidedTour />
            </TutorialProvider>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
